import type { MeasureItem, MeasureType } from '@/components/organisms/MeasureCard/types';

export type CompareDirection = 'decrease' | 'increase' | 'none';

export type MeasureCompareResult = {
  direction: CompareDirection;
  good: boolean | null;
  percentage: number;
};

// every raw quantity is "less is better"; separation is the only rate where higher is better
const LOWER_IS_BETTER: Record<MeasureType, boolean> = {
  amount: true,
  co2_transport: true,
  distance: true,
  hazardous: true,
  separation: false,
  trips: true,
};

/** Compares a base (current) period value against a comparison (past) period value for a MeasureCard */
export function getMeasureCompare(base: number, comparison: number, type: MeasureType): MeasureCompareResult {
  if (base === comparison) return { direction: 'none', good: null, percentage: 0 };

  const direction: CompareDirection = base > comparison ? 'increase' : 'decrease';
  const lowerIsBetter = LOWER_IS_BETTER[type];
  const good = direction === 'decrease' ? lowerIsBetter : !lowerIsBetter;

  // separation is already a percentage, so its change is a plain point difference
  if (type === 'separation') return { direction, good, percentage: base - comparison };

  if (!base) return { direction: 'none', good: null, percentage: 0 };

  return { direction, good, percentage: ((base - comparison) / base) * 100 };
}

export type MeasureCardConfig<TData> = {
  description: string;
  formatValue: (value: number) => string;
  getValue: (data: TData) => number;
  measureType: MeasureType;
  question: string;
  secondaryTitle: string;
};

/** Builds MeasureItems for a set of KPIs, attaching a period compare to each when one is available */
export function buildMeasureItems<TData>(
  configs: MeasureCardConfig<TData>[],
  data: TData | undefined,
  compareData: TData | undefined,
  canCompare: boolean
): MeasureItem[] {
  return configs.map(({ description, formatValue, getValue, measureType, question, secondaryTitle }) => {
    const base = data ? getValue(data) : 0;
    const comparison = canCompare && compareData ? getValue(compareData) : 0;

    return {
      title: question,
      secondaryTitle,
      amount: formatValue(base),
      description,
      type: measureType,
      ...(canCompare && compareData
        ? { compare: getMeasureCompare(base, comparison, measureType), compareAmount: formatValue(comparison) }
        : {}),
    };
  });
}
